// Scenario 5 (Pricing + Transit Investment), its transit part: deck.gl layers. getUpgradedLinesLayer draws
// the modelled lines (scenario5/transit_upgrades.json); the rest draw the illustrative bundle for the Workshop maps.
//
//   import { loadTransitScenario, getTransitInvestmentLayers } from '../../utils/transitScenarioLayers';
//   const data = await loadTransitScenario('/data/transit/scenario5.json');   // fetch once, reuse for both sizes
//   const layers = getTransitInvestmentLayers(data, { compact: true });       // add to the layer list you already build
//
// The map itself (basemap, controller, view state) stays with the caller, so the same layers work on the
// big map (WorkshopMap) and the thumbnail (ThumbMap, no basemap). Data comes from
// scripts/rank_routes.py --bundle, one file of about 600 KB.
import { PolygonLayer, GeoJsonLayer } from '@deck.gl/layers';
import { cellToBoundary } from 'h3-js';
import { showTip } from './transitInvestment';

const TEAL = [29, 200, 160];
const AMBER = [255, 196, 40];

export async function loadTransitScenario(url) {
  const doc = await fetch(url).then((r) => {
    if (!r.ok) throw new Error(`transit scenario: ${r.status} ${url}`);
    return r.json();
  });
  const gapShare = doc.meta.gap_low_income_share;
  doc.cells = doc.hexes.map(([h3id, hh, low, before, add]) => ({
    h3id, hh, low, before, add, share: hh > 0 ? low / hh : null,
    gap: hh >= 100 && low / hh >= gapShare && before < doc.meta.gap_trips,
    polygon: cellToBoundary(h3id).map(([lat, lng]) => [lng, lat]),
  }));
  return doc;
}

/**
 * options.compact  thumbnail size: fainter cells, thinner lines, no hover.
 * options.view     'access' (default): teal where a hexagon gains weekday trips, amber outline if mostly low-income.
 *                  'gaps': red = still underserved, teal = closed by the investment.
 * Returns [hexLayer, routeLayer]; ids are prefixed so they never clash with the caller's layers.
 */
export function getTransitInvestmentLayers(data, { compact = false, view = 'access' } = {}) {
  if (!data || !data.cells) return [];
  const gapShare = data.meta.gap_low_income_share;
  const need = data.meta.gap_trips;
  const dim = compact ? 0.7 : 1;
  const fill = (d) => {
    if (view === 'gaps') {
      if (!(d.share != null && d.share >= gapShare && d.hh >= 100)) return [120, 120, 120, 14];
      if (d.before >= need) return [140, 90, 200, 60];
      return d.before + d.add >= need ? [...TEAL, 220] : [230, 90, 60, 200];
    }
    if (d.add <= 0) return [120, 120, 120, d.hh >= 100 ? 24 : 8];
    return [...TEAL, Math.round((50 + Math.min(1, d.add / 60) * 170) * dim)];
  };
  const mostlyLow = (d) => view === 'access' && d.add > 0 && d.share != null && d.share >= gapShare;
  const hexLayer = new PolygonLayer({
    id: 'transit-inv-hex',
    data: data.cells,
    pickable: !compact,
    stroked: !compact,
    filled: true,
    lineWidthUnits: 'pixels',
    getPolygon: (d) => d.polygon,
    getFillColor: fill,
    getLineColor: (d) => (mostlyLow(d) ? [...AMBER, 230] : [255, 255, 255, 18]),
    getLineWidth: (d) => (mostlyLow(d) ? 2 : 0.5),
    updateTriggers: { getFillColor: [view, compact], getLineColor: [view], getLineWidth: [view] },
  });
  const routeLayer = new GeoJsonLayer({
    id: 'transit-inv-routes',
    data: data.routes,
    pickable: false,
    lineWidthUnits: 'pixels',
    getLineWidth: compact ? 1.5 : 4,
    lineWidthMinPixels: compact ? 1 : 3,
    getLineColor: [...AMBER, 255],
    updateTriggers: { getLineWidth: [compact] },
  });
  return [hexLayer, routeLayer];
}

/** Headline numbers for a caption or stat tile: { lines, extraTrips, extraPct, lowBetter, gapBefore, gapAfter }. */
export function transitInvestmentSummary(data) {
  const s = data.stats;
  return {
    lines: s.lines_funded, extraTrips: s.extra_trips, extraPct: s.extra_pct,
    lowBetter: s.low_income_households_better_off, gapBefore: s.gap_hexes_before, gapAfter: s.gap_hexes_after,
  };
}

/**
 * Where to point a map at the scenario: { longitude, latitude, zoom } for the bundled lines and hexagons,
 * for a thumbnail whose own centre (e.g. the pricing zone) is not set yet or lies outside the transit area.
 * `width` / `height` are the map size in pixels (default: a 320 x 200 thumbnail).
 */
export function getTransitScenarioView(data, { width = 320, height = 200, padding = 0.1 } = {}) {
  let minX = 180, maxX = -180, minY = 90, maxY = -90;
  const visit = (c) => {
    if (typeof c[0] === 'number') {
      if (c[0] < minX) minX = c[0];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[1] > maxY) maxY = c[1];
    } else c.forEach(visit);
  };
  (data.routes ? data.routes.features : []).forEach((f) => visit(f.geometry.coordinates));
  if (minX > maxX) return { longitude: -122.42, latitude: 37.77, zoom: 10 };
  const lng = (minX + maxX) / 2;
  const lat = (minY + maxY) / 2;
  // Web-mercator fit: 512 px tile at zoom 0.
  const mercY = (y) => Math.log(Math.tan(Math.PI / 4 + (y * Math.PI) / 360));
  const spanX = ((maxX - minX) / 360) * (1 + 2 * padding);
  const spanY = ((mercY(maxY) - mercY(minY)) / (2 * Math.PI)) * (1 + 2 * padding);
  const zoom = Math.min(
    Math.log2(width / 512 / Math.max(spanX, 1e-6)),
    Math.log2(height / 512 / Math.max(spanY, 1e-6)),
  );
  return { longitude: lng, latitude: lat, zoom: Math.max(0, Math.min(14, zoom)) };
}

/** How scenario 5's budget picked a line: by expected switches to transit per dollar, or,
 *  with the share set aside for equity, by low-income households reached per dollar. */
export const PICKED_BY = {
  shifts_per_dollar: { label: 'shifts/$', color: [124, 198, 68], what: 'expected switches to transit per dollar' },
  low_income_reach_per_dollar: { label: 'equity', color: [79, 195, 247], what: 'low-income households reached per dollar (the equity share of the budget)' },
};
const MIN = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pickOf = (l) => PICKED_BY[l.picked_by] || { label: l.picked_by, color: [200, 200, 200], what: l.picked_by };

/** Scenario 5's upgraded lines (transit_upgrades.json) on the map, coloured by how each
 *  was picked: every route_id of a line, from routes.geojson. Null when none is drawn. */
export function getUpgradedLinesLayer(routesGeo, upgrades) {
  if (!routesGeo?.features || !upgrades?.lines) return null;
  const lineOf = new Map();
  upgrades.lines.forEach((l) => l.route_ids.forEach((r) => lineOf.set(r, l)));
  const features = routesGeo.features.filter((f) => lineOf.has(f.properties.route_id));
  if (features.length === 0) return null;
  return new GeoJsonLayer({
    id: 'inv-s5-routes',
    data: { type: 'FeatureCollection', features },
    pickable: true,
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: 3,
    getLineWidth: 4,
    getLineColor: (f) => [...pickOf(lineOf.get(f.properties.route_id)).color, 255],
    onHover: ({ object, x, y }) => {
      if (!object) return showTip(0, 0, null);
      const l = lineOf.get(object.properties.route_id);
      return showTip(x, y,
        `<b>${l.line}</b> · ${l.agency}<br/>Peak headway ${MIN.format(l.peak_headway_min)} → ${MIN.format(l.new_headway_min)} min, `
        + `${MIN.format(l.wait_saving_min)} min less waiting`
        + `<br/>${USD.format(l.extra_cost_per_weekday_usd)} a weekday · ${Math.round(l.marginal_shifts_per_weekday)} expected switches`
        + `<br/>Picked by ${pickOf(l).what}`);
    },
  });
}
